import { useState } from 'react';
import useAsync from '../../components/hooks/useAsync';
import { usePersistedState } from '@aha-app/aha-shared';
import type IntegrationsController from '../../controllers/IntegrationsController';
import type {
  RecordFieldData,
  IntegrationProject,
  IntegrationRecordField,
  JiraWhiteboardIntegration,
  NewRecords,
  IntegrationSite,
  CreateRecord,
} from '../../shared-types';
import trackEvent from '../../utils/analytics';

interface State {
  selectedProject?: IntegrationProject;
  selectedIssueTypeId?: string;
  fieldData: RecordFieldData;
  records: NewRecords;

  // field definitions keyed by project and issue type id
  fields: Record<string, Record<string, IntegrationRecordField[]>>;
}

interface Props {
  integrationsController: IntegrationsController;
  integration: JiraWhiteboardIntegration;
  data: CreateRecord[];
}

export interface IntegrationRecordsModalController {
  state: State;
  selectedSite?: IntegrationSite;
  sites: IntegrationSite[] | undefined;
  getSelectedSite(): IntegrationSite | undefined;
  getCurrentFields(): IntegrationRecordField[] | undefined;
  recordsValid(): boolean;
  fieldsValid(): boolean;
  actionCreateRecords(): Promise<unknown>;
  actionSetSelectedSite(site: IntegrationSite): void;
  actionSetSelectedProject(project: IntegrationProject): void;
  actionSetSelectedIssueTypeId(issueTypeId: string): void;
  actionUpdateSummary(shapeId: string, summary: string): void;
  actionUpdateDescription(shapeId: string, description: string): void;
  actionUpdateField(name: string, value: string | string[]): void;
}

const useIntegrationRecordsModalController = ({
  data,
  integration,
  integrationsController,
}: Props): IntegrationRecordsModalController => {
  const [state, setState] = useState<State>(() => ({
    fieldData: {},
    records: data.reduce<NewRecords>((acc, cur) => {
      acc[cur.id] = { summary: cur.text };
      return acc;
    }, {}),
    fields: {},
  }));
  const [selectedSiteId, setSelectedSiteId] = usePersistedState(
    `${integration.service}-selected-site`,
    String,
    true
  );

  const updateState = (update: Partial<State>) =>
    setState(current => ({ ...current, ...update }));

  const updateRecord = (
    shapeId: string,
    update: { summary?: string; description?: string }
  ) =>
    // :grimace:
    setState(current => ({
      ...current,
      records: {
        ...current.records,
        [shapeId]: {
          ...current.records[shapeId],
          ...update,
        },
      },
    }));

  const loadFields = async (projectId: string, issueTypeId: string) => {
    const result = await integration.getRecordFields({
      key: { siteId: selectedSiteId, projectId, issueTypeId },
      options: { authenticate: true },
    });

    if (result.status === 'error') {
      console.error('Error loading fields', result.error);
    } else {
      setState(current => ({
        ...current,
        fields: {
          ...current.fields,
          [projectId]: {
            ...current.fields[projectId],
            [issueTypeId]: result.data.fields,
          },
        },
      }));
    }
  };

  const getCurrentFields = () => {
    const { selectedProject, selectedIssueTypeId, fields } = state;

    if (!selectedProject || !selectedIssueTypeId) {
      return;
    }

    if (!fields[selectedProject.id]) {
      fields[selectedProject.id] = {};
    }

    if (!fields[selectedProject.id][selectedIssueTypeId]) {
      // kick off an update to fill in the requested data. Once it completes, it'll trigger a reactive render
      // in the component that called this function
      void loadFields(selectedProject.id, selectedIssueTypeId);
    }

    return fields[selectedProject.id]?.[selectedIssueTypeId]?.filter(
      f => f.name !== 'description'
    );
  };

  const recordsValid = () => {
    const { records } = state;
    return Object.values(records).every(record => record.summary.trim());
  };

  const fieldsValid = () => {
    const { fieldData } = state;
    return !!getCurrentFields()
      ?.filter(field => field.required)
      .every(field => {
        switch (field.type.type) {
          case 'text':
            return !!fieldData?.[field.name];
          case 'tags':
          case 'select':
            if (field.type.type === 'tags' || field.type.multiple) {
              return !!(
                Array.isArray(fieldData?.[field.name]) &&
                fieldData?.[field.name].length
              );
            } else {
              return !!fieldData?.[field.name];
            }
          default:
            console.warn('Unsupported schema type');
            return true;
        }
      });
  };

  const { data: sites } = useAsync(async () => {
    const result = await integration.getSites({
      options: { authenticate: true },
    });

    return result.status === 'success' ? result.data.sites : [];
  });

  return {
    state,
    sites,
    getCurrentFields,
    recordsValid,
    fieldsValid,

    getSelectedSite() {
      if (sites?.length === 1) {
        return sites[0];
      }
      return sites?.find(site => site.id === selectedSiteId);
    },

    async actionCreateRecords() {
      const { fieldData, records, selectedProject, selectedIssueTypeId } =
        state;

      if (
        !selectedSiteId ||
        !selectedProject ||
        !selectedIssueTypeId ||
        !recordsValid() ||
        !fieldsValid()
      ) {
        console.warn('Invalid form data');
        return;
      }
      await integrationsController.actionCreateRecords(
        'jira',
        {
          siteId: selectedSiteId,
          projectId: selectedProject.id,
          issueTypeId: selectedIssueTypeId,
        },
        records,
        fieldData
      );

      const issueType = selectedProject.recordTypes.find(
        issue => issue.id === selectedIssueTypeId
      )?.name;

      trackEvent(
        'Feature-7351856624664895072',
        'User converts whiteboard shape to Jira',
        {
          'Issue type': issueType,
        }
      );
    },

    actionSetSelectedSite(site) {
      setSelectedSiteId(site.id);
      updateState({
        selectedProject: undefined,
        selectedIssueTypeId: undefined,
      });
    },

    actionSetSelectedProject(project) {
      updateState({
        selectedProject: project,
        selectedIssueTypeId: undefined,
      });
    },

    actionSetSelectedIssueTypeId(issueTypeId) {
      updateState({
        selectedIssueTypeId: issueTypeId,
      });
    },

    actionUpdateSummary(shapeId, summary) {
      updateRecord(shapeId, { summary });
    },

    actionUpdateDescription(shapeId, description) {
      updateRecord(shapeId, { description });
    },

    actionUpdateField(name, value) {
      setState(current => ({
        ...current,
        fieldData: {
          ...current.fieldData,
          [name]: value,
        },
      }));
    },
  };
};

export default useIntegrationRecordsModalController;
